import { cva, VariantProps } from 'class-variance-authority';
import styles from './padding.module.css';
import type { InputType } from 'storybook/internal/types';

const paddingOptions = [
  'none',
  '5xs',
  '4xs',
  '3xs',
  '2xs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  '6xl',
] as const;

export const paddingVariants = cva('', {
  variants: {
    padding: {
      none: styles['padding--none'],
      '5xs': styles['padding--5xs'],
      '4xs': styles['padding--4xs'],
      '3xs': styles['padding--3xs'],
      '2xs': styles['padding--2xs'],
      xs: styles['padding--xs'],
      sm: styles['padding--sm'],
      md: styles['padding--md'],
      lg: styles['padding--lg'],
      xl: styles['padding--xl'],
      '2xl': styles['padding--2xl'],
      '3xl': styles['padding--3xl'],
      '4xl': styles['padding--4xl'],
      '5xl': styles['padding--5xl'],
      '6xl': styles['padding--6xl'],
    },
    paddingX: {
      none: styles['padding-x--none'],
      '5xs': styles['padding-x--5xs'],
      '4xs': styles['padding-x--4xs'],
      '3xs': styles['padding-x--3xs'],
      '2xs': styles['padding-x--2xs'],
      xs: styles['padding-x--xs'],
      sm: styles['padding-x--sm'],
      md: styles['padding-x--md'],
      lg: styles['padding-x--lg'],
      xl: styles['padding-x--xl'],
      '2xl': styles['padding-x--2xl'],
      '3xl': styles['padding-x--3xl'],
      '4xl': styles['padding-x--4xl'],
      '5xl': styles['padding-x--5xl'],
      '6xl': styles['padding-x--6xl'],
    },
    paddingY: {
      none: styles['padding-y--none'],
      '5xs': styles['padding-y--5xs'],
      '4xs': styles['padding-y--4xs'],
      '3xs': styles['padding-y--3xs'],
      '2xs': styles['padding-y--2xs'],
      xs: styles['padding-y--xs'],
      sm: styles['padding-y--sm'],
      md: styles['padding-y--md'],
      lg: styles['padding-y--lg'],
      xl: styles['padding-y--xl'],
      '2xl': styles['padding-y--2xl'],
      '3xl': styles['padding-y--3xl'],
      '4xl': styles['padding-y--4xl'],
      '5xl': styles['padding-y--5xl'],
      '6xl': styles['padding-y--6xl'],
    },
    paddingTop: {
      none: styles['padding-top--none'],
      '5xs': styles['padding-top--5xs'],
      '4xs': styles['padding-top--4xs'],
      '3xs': styles['padding-top--3xs'],
      '2xs': styles['padding-top--2xs'],
      xs: styles['padding-top--xs'],
      sm: styles['padding-top--sm'],
      md: styles['padding-top--md'],
      lg: styles['padding-top--lg'],
      xl: styles['padding-top--xl'],
      '2xl': styles['padding-top--2xl'],
      '3xl': styles['padding-top--3xl'],
      '4xl': styles['padding-top--4xl'],
      '5xl': styles['padding-top--5xl'],
      '6xl': styles['padding-top--6xl'],
    },
    paddingRight: {
      none: styles['padding-right--none'],
      '5xs': styles['padding-right--5xs'],
      '4xs': styles['padding-right--4xs'],
      '3xs': styles['padding-right--3xs'],
      '2xs': styles['padding-right--2xs'],
      xs: styles['padding-right--xs'],
      sm: styles['padding-right--sm'],
      md: styles['padding-right--md'],
      lg: styles['padding-right--lg'],
      xl: styles['padding-right--xl'],
      '2xl': styles['padding-right--2xl'],
      '3xl': styles['padding-right--3xl'],
      '4xl': styles['padding-right--4xl'],
      '5xl': styles['padding-right--5xl'],
      '6xl': styles['padding-right--6xl'],
    },
    paddingBottom: {
      none: styles['padding-bottom--none'],
      '5xs': styles['padding-bottom--5xs'],
      '4xs': styles['padding-bottom--4xs'],
      '3xs': styles['padding-bottom--3xs'],
      '2xs': styles['padding-bottom--2xs'],
      xs: styles['padding-bottom--xs'],
      sm: styles['padding-bottom--sm'],
      md: styles['padding-bottom--md'],
      lg: styles['padding-bottom--lg'],
      xl: styles['padding-bottom--xl'],
      '2xl': styles['padding-bottom--2xl'],
      '3xl': styles['padding-bottom--3xl'],
      '4xl': styles['padding-bottom--4xl'],
      '5xl': styles['padding-bottom--5xl'],
      '6xl': styles['padding-bottom--6xl'],
    },
    paddingLeft: {
      none: styles['padding-left--none'],
      '5xs': styles['padding-left--5xs'],
      '4xs': styles['padding-left--4xs'],
      '3xs': styles['padding-left--3xs'],
      '2xs': styles['padding-left--2xs'],
      xs: styles['padding-left--xs'],
      sm: styles['padding-left--sm'],
      md: styles['padding-left--md'],
      lg: styles['padding-left--lg'],
      xl: styles['padding-left--xl'],
      '2xl': styles['padding-left--2xl'],
      '3xl': styles['padding-left--3xl'],
      '4xl': styles['padding-left--4xl'],
      '5xl': styles['padding-left--5xl'],
      '6xl': styles['padding-left--6xl'],
    },
  },
});

export type PaddingVariants = VariantProps<typeof paddingVariants>;

export const paddingArgTypes: Record<string, InputType> = {
  padding: {
    control: 'select',
    options: paddingOptions,
    description:
      '**[Responsive]** Padding on all sides using design system spacing tokens',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  paddingX: {
    control: 'select',
    options: paddingOptions,
    description: '**[Responsive]** Horizontal padding (left and right)',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  paddingY: {
    control: 'select',
    options: paddingOptions,
    description: '**[Responsive]** Vertical padding (top and bottom)',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  paddingTop: {
    control: 'select',
    options: paddingOptions,
    description: '**[Responsive]** Padding on the top side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  paddingRight: {
    control: 'select',
    options: paddingOptions,
    description: '**[Responsive]** Padding on the right side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  paddingBottom: {
    control: 'select',
    options: paddingOptions,
    description: '**[Responsive]** Padding on the bottom side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
  paddingLeft: {
    control: 'select',
    options: paddingOptions,
    description: '**[Responsive]** Padding on the left side',
    table: {
      type: { summary: 'Spacing' },
      category: 'Spacing',
    },
  },
};
